import React, {useEffect, useState} from 'react';

import {Calendar} from '@pb/kit-component-calendar';
import {CustomGroup, CustomGroupItem} from '@pb/kit-component-input-v2';
import {formatDate} from '@pb/kit-formatters';

import {FilterHeader} from './filter-header';
import {Props as SortableHeaderProps} from './sortable-header';
import {DateRange} from './typedefs';

type OwnProps = {
    filters: DateRange,
    setFilter: (value: DateRange) => void,
    allowFutureDate?: boolean,
};

type Props = OwnProps & Omit<SortableHeaderProps, 'contentAfter'>;

const INITIAL_VALUE: DateRange = {from: '', to: ''};
const DATE_FROM_FIELD = 'from';
const DATE_TO_FIELD = 'to';
const DATE_FORMAT_DISPLAY = 'dd-MMM-yyyy';
const OUTPUT_DATE_FORMAT = 'DD-MMM-YYYY';

export const DateRangeFilterHeader = (props: Props) => {
    const {
        caption,
        onSort,
        isSorted,
        filters,
        setFilter,
        allowFutureDate,
    } = props;

    const [value, setValue] = useState(filters || INITIAL_VALUE);
    const {from, to} = value;

    const selectedFromDate = from ? new Date(from) : undefined;
    const selectedToDate = to ? new Date(to) : undefined;
    const maxDate = allowFutureDate ? undefined : new Date();

    useEffect(() => {
        if (filters) {
            setValue(filters);
        }
    }, [filters]);

    const handleClose = (onClose?: () => void) => {
        if (onClose) {
            onClose();
        }
    };

    const onFilterApply = (onClose?: () => void) => {
        setFilter(value);
        handleClose(onClose);
    };

    const onCancel = (onClose?: () => void) => {
        setValue(filters);
        handleClose(onClose);
    };

    const resetFilters = (onClose?: () => void) => {
        setFilter(INITIAL_VALUE);
        setValue(INITIAL_VALUE);
        handleClose(onClose);
    };

    const handleValueChange = (name: string, date?: Date) => {
        if (date) {
            setValue({...value, [name]: formatDate(date, OUTPUT_DATE_FORMAT, DATE_FORMAT_DISPLAY)});
        }
    };

    const onChangeFromDate = (date?: Date) => {
        handleValueChange(DATE_FROM_FIELD, date);
    };

    const onChangeToDate = (date?: Date) => {
        handleValueChange(DATE_TO_FIELD, date);
    };

    return (
        <FilterHeader {...{
            isFilterApplied: !!filters?.from || !!filters?.to,
            onFilterApply,
            onCancel,
            resetFilters,
            caption,
            onSort,
            isSorted,
        }}>
            <CustomGroup>
                <CustomGroupItem label="From">
                    <Calendar
                        selected={selectedFromDate}
                        clearableValue={selectedFromDate}
                        onChange={onChangeFromDate}
                        dateFormat={DATE_FORMAT_DISPLAY}
                        maxDate={selectedToDate || maxDate}
                    />
                </CustomGroupItem>
                <CustomGroupItem label="To">
                    <Calendar
                        selected={selectedToDate}
                        clearableValue={selectedToDate}
                        minDate={selectedFromDate}
                        maxDate={maxDate}
                        dateFormat={DATE_FORMAT_DISPLAY}
                        onChange={onChangeToDate}
                    />
                </CustomGroupItem>
            </CustomGroup>
        </FilterHeader>
    );
};
